<template>
  <div>
    <section>
      <transition name="fade">
        <verify-payment-popup v-if="showRedirectVerifyPopup" :key="refreshVerifyPopup" />
        <delete-account-popup
          v-if="cancelSubscriptionCondition"
          cancel-subscription-prop
          :team="team"
          :active-members-count="activeMembersCount"
          @close="cancelSubscriptionCondition = false"
          @account-deleted="cancel"
        />
      </transition>
      <skeleton-setting v-if="loading" class="single-setting" :title="$t('subscriptionPlan')" />
      <single-setting
        v-else
        id="subscription-plan"
        class="single-setting"
        :icon="require('@/assets/_desktop/_settings/_icons/Subscription.svg')"
        :title="$t('subscriptionPlan')"
      >
        <field-big-text :title="planTitle" :title-addon="planTitleAddon" :description="planTagline" :total="planTotal" :css-class="'dark'" />
        <div v-if="upcomingInvoiceAmount != ''" id="next-invoice-details">
          {{ $t('nextInvoiceXonY', { x: upcomingInvoiceAmount, y: upcomingInvoiceDateFormatted }) }}
        </div>
        <div id="plan-buttons">
          <button v-if="showRestorePlan" class="clavo-button primary" @click="restorePlanClicked">
            {{ $t('restorePlan') }}
          </button>
          <button v-if="showUpgradeNow" class="clavo-button primary" @click="upgradePlanClicked">
            {{ $t('upgradePlan') }}
          </button>
          <button v-if="showSwitchToYearlyBilling" class="clavo-button secondary" @click="switchToPlanClicked">
            {{ $t('switchToYearlyBilling', { amount: howMuchSave }) }}
          </button>
          <!-- <button
            v-if="showSwitchToMonthlyBilling"
            class="clavo-button secondary"
            @click="switchToPlanClicked"
          >
            {{ $t('switchToMonthlyBilling') }}
          </button> -->
          <span v-if="showCancelPlan" class="clavo-link secondary" @click="cancelSubscriptionCondition = true">
            {{ $t('cancelPlan') }}
          </span>
        </div>
      </single-setting>

      <skeleton-setting v-if="loading" class="single-setting" :title="$t('paymentMethod')" />
      <single-setting
        v-else
        id="payment-method"
        class="single-setting"
        :warning="paymentFailedAlert"
        :icon="require('@/assets/_desktop/_settings/_icons/Payment Method.svg')"
        :title="$t('paymentMethod')"
        :description="$t('paymentMethodDesc')"
      >
        <card-payment-section-stripe
          v-if="showChangeCard"
          :team-id="team.teamId"
          :class="'payment-method-update'"
          @close="changeCardClicked"
          @save="emittingSaveCard"
        />
        <div v-if="paymentMethodFetched && !showChangeCard" class="payment-method-inside">
          <div id="card-details">
            {{ cardBrand + ' ' + cardNumber }}
          </div>
          <div v-if="pendingPlan && pendingPlan == plan" id="subscription-failed">
            {{ $t('paymentForAdditionalMembersFailed') }}
            <strong>{{ $t('pleaseUpdateYourPaymentDetails') }}</strong>
          </div>
          <div v-if="pendingPlan && pendingPlan != plan" id="subscription-failed">
            {{ $t('upgradePlanFailed') }}
            <strong>{{ $t('pleaseUpdateYourPaymentDetails') }}</strong>
          </div>
          <div v-else-if="recurringPaymentFailed" id="subscription-failed">
            {{ $t('recurringSubscriptionPaymentFailed') }}
            <strong>{{ $t('pleaseUpdateYourPaymentDetails') }}</strong>
          </div>
          <div v-else-if="initialPaymentFailed" id="subscription-failed">
            {{ $t('initialPaymentFailed') }}
            <strong>{{ $t('pleaseUpdateYourPaymentDetails') }}</strong>
          </div>
          <!-- <div
            v-else-if="upcomingInvoiceAmount != ''"
            id="next-invoice-details"
          >
            {{ $t('nextInvoiceXonY', { x: upcomingInvoiceAmount, y: upcomingInvoiceDateFormatted }) }}
          </div> -->
          <div id="payment-buttons">
            <button v-if="pendingPlan || recurringPaymentFailed || initialPaymentFailed" class="clavo-button primary first" @click="retryPaymentClicked">
              {{ $t('retryPayment') }}
            </button>
            <button class="clavo-button secondary" @click="changeCardClicked">
              {{ $t('changeCard') }}
            </button>
            <span class="clavo-link secondary">
              {{ $t('redeemCoupon') }}
            </span>
          </div>
        </div>
        <div v-if="!paymentMethodFetched" class="undefined">
          {{ $t('noDefinedPaymentMethods') }}
        </div>
      </single-setting>
      <skeleton-setting v-if="loading" class="single-setting" :title="$t('paymentMethod')" />
      <single-setting
        v-else
        id="billing-information"
        class="single-setting"
        :icon="require('@/assets/_desktop/_settings/_icons/Billiing Info.svg')"
        :title="$t('billingInformation')"
        :description="$t('billingInformationDesc')"
      >
        <billing-information
          v-if="showUpdateInformation"
          :team-id="team.teamId"
          :company-name-prop="companyName"
          :address-line1-prop="addressLine1"
          :address-line2-prop="addressLine2"
          :zip-code-prop="postalCode"
          :city-prop="city"
          :country-code-prop="country"
          :vat-id-prop="vatId"
          :business-purchase-prop="businessPurchase"
          :state-province-prop="stateProvince"
          @save="refreshBillingInformation"
          @close="updateBillingInformationClicked"
        />
        <template v-if="!showUpdateInformation" v-slot:one-column-no-gaps>
          <div v-if="!isBillingInfo" class="undefined">
            {{ $t('noBillingInformation') }}
          </div>
          <div v-else :key="showUpdateInformation">
            <div>
              {{ companyName }}<br />
              {{ addressLine1 }}<br v-if="addressLine2" />
              {{ addressLine2 }}<br />
              {{ postalCode + ' ' + city }}<br />
              {{ $store.state.countries.countries[country].name }}
            </div>
            <div v-if="businessPurchase">
              <span class="bold">{{ (country === 'PL' ? $t('nip') : $t('vatId')) + ':' }}</span>
              {{ vatId }}
            </div>
          </div>

          <button v-if="showUpdateBillingButton" class="clavo-button secondary" @click="updateBillingInformationClicked">
            {{ $t('update') }}
          </button>
        </template>
      </single-setting>
      <skeleton-setting v-if="loadingInvoices" class="single-setting" :title="$t('paymentMethod')" />
      <single-setting
        v-else
        id="invoice-history"
        class="single-setting"
        :icon="require('@/assets/_desktop/_settings/_icons/Invoices.svg')"
        :title="$t('invoiceHistory')"
        :description="$t('invoiceHistoryDesc', { email: email })"
      >
        <div v-if="invoices.length > 0" class="list">
          <div class="headers">
            <div class="header">
              {{ $t('monthCapitalized') }}
            </div>
            <div class="header">
              {{ $t('valueCapitalized') }}
            </div>
            <div class="header" />
          </div>
          <div v-for="inv in invoices" :key="inv.id" class="row">
            <div class="item date">
              {{ inv.date }}
            </div>
            <div class="item value">
              {{ inv.value }}
            </div>
            <a :href="inv.pdf" class="item pdf clavo-link primary" :class="{ inactive: !inv.pdf }">
              {{ inv.pdf ? $t('pdfCapitalized') : '' }}
            </a>
          </div>
        </div>
        <p v-if="!(invoices.length > 0)">
          {{ $t('noInvoices') }}
        </p>
      </single-setting>
    </section>
  </div>
</template>

<script>
import SingleSetting from '@/components/Settings/SingleSetting'
import FieldBigText from '@/components/UI/FieldBigText'
import ParseFunctions from '@/components/ParseFunctions'
import EventBus from '@/EventBus'
import Payment from '@/components/Billing/Payment'
import SkeletonSetting from '@/components/Settings/SkeletonSetting'
import Constants from '@/components/Constants'
import CardPaymentSectionStripe from '@/components/Billing/CardPaymentSectionStripe'
import BillingInformation from '@/components/Billing/BillingInformation'
import DeleteAccountPopup from '@/components/Settings/DeleteAccountPopup'
import { getlFormatDate } from '@/components/utils'
import VerifyPaymentPopup from '@/components/Billing/VerifyPaymentPopup'
import BillingUtils from '@/components/Billing/BillingUtils'

export default {
  name: 'TeamBilling',
  components: {
    DeleteAccountPopup,
    SingleSetting,
    FieldBigText,
    SkeletonSetting,
    CardPaymentSectionStripe,
    BillingInformation,
    VerifyPaymentPopup,
  },
  mixins: [ParseFunctions, Constants, BillingUtils],
  props: {
    team: Object,
  },
  data() {
    return {
      activeMembersCount: 0,
      pricePerMemberNoTaxFormatted: 0,
      yearlyBillingCost: 0,
      stars: '',
      invoiceAmount: '',
      invoiceDate: '',
      companyName: '',
      addressLine1: '',
      addressLine2: '',
      postalCodeAndCity: '',
      stateProvince: '',
      postalCode: '',
      city: '',
      isBillingInfo: false,
      country: '',
      currency: '',
      vatId: '',
      email: '',
      invoices: [],
      cardNumber: '',
      cardLast4: '',
      cardBrand: '',
      paymentMethodFetched: false,
      paymentStatus: '',
      subscriptionStatus: '',
      plan: '',
      planTotal: '',
      upcomingInvoiceDateFormatted: '',
      upcomingInvoiceAmountFormatted: '',
      pricePerMemberWithTaxFormatted: '',
      loading: true,
      loadingInvoices: true,
      isTrial: false,
      cancelSubscriptionCondition: false,
      paymentId: '',
      planTitle: '',
      planTitleAddon: '',
      planTagline: '',
      showUpgradeNow: false,
      showSwitchToYearlyBilling: true,
      showSwitchToMonthlyBilling: false,
      showCancelPlan: true,
      showRestorePlan: false,
      showChangeCard: false,
      showUpdateInformation: false,
      showUpdateBillingButton: false,
      pendingPlan: undefined,
      recurringPaymentFailed: false,
      initialPaymentFailed: false,
      refreshIntervalId: '',
      paymentFailedAlert: false,
      showRedirectVerifyPopup: false,
      redirectUrl: '',
      refreshVerifyPopup: false,
      businessPurchase: false,
    }
  },
  async created() {
    await this.load()
  },
  mounted() {
    this.mountStripeScript()
  },
  methods: {
    async load() {
      let allDataOK = false
      let updateOk = await this.updatePaymentDetails()
      if (updateOk) {
        // only if properly get payment details then try to get invoices
        let res = await this.getInvoices(this.team.teamId, this.country)
        if (res.status === 'ok') {
          this.invoices = res.invoices
          allDataOK = true
        } else {
          this.showServerError(res)
        }
        this.loadingInvoices = false
      }
      if (allDataOK) {
        this.allDataRefreshed = true
      } else {
        this.allDataRefreshed = false
      }
    },
    updateBillingInformationClicked() {
      this.showUpdateInformation = !this.showUpdateInformation
    },
    async refreshBillingInformation(businessPurchase) {
      this.loading = true
      this.showUpdateInformation = !this.showUpdateInformation
      this.businessPurchase = businessPurchase
      await this.updatePaymentDetails()
      this.loading = false
    },
    changeCardClicked() {
      this.showChangeCard = !this.showChangeCard
    },
    async retryPaymentClicked() {
      this.loading = true
      var res = await this.retryPayment(this.team.teamId)
      this.loading = false
      // console.log('RETRIED -----------<>', res)
      if (res.status == 'ok') {
        if (res.invoiceStatus == 'paid') {
          EventBus.$emit('new-info-bar-msg', { msg: this.$t('subscriptionRestoredSuccessfuly') })
        } else if (res.invoiceStatus == 'invoice_payment_intent_requires_action') {
          this.showRedirectVerifyPopup = true
          this.refreshIntervalId = window.setInterval(() => {
            this.processingRetryPayment()
          }, 4000)
        } else {
          this.showPaymentError(res)
        }
      } else {
        this.showPaymentError(res)
      }
      await this.updatePaymentDetails()
      this.getTeamById(this.team.teamId).then((team) => {
        this.$store.commit('setSelectedTeam', team)
      })
    },
    async processingRetryPayment() {
      let res = await this.getPaymentStatus(this.team.teamId, null)

      if (res.redirectUrl) {
        window.clearInterval(this.refreshIntervalId)
        this.redirectUrl = res.redirectUrl
        this.refreshVerifyPopup = true
      } else {
        if (res.subscriptionStatus === 'active' && !this.pendingPlan) {
          window.clearInterval(this.refreshIntervalId)
          this.showRedirectVerifyPopup = false
        } else if (res.subscriptionStatus != 'incomplete' && res.subscriptionStatus != 'active') {
          this.showRedirectVerifyPopup = false
        }
      }
    },
    async emittingSaveCard(changedCard) {
      this.showChangeCard = !this.showChangeCard
      this.cardNumber = '**** **** **** ' + changedCard.card.last4
      this.cardBrand = changedCard.card.brand.charAt(0).toUpperCase() + changedCard.card.brand.slice(1)
      await this.updatePaymentDetails()
    },
    setUpgradePlanButtonsVisibility(params) {
      this.showUpgradeNow = params.showUpgradeNow ? params.showUpgradeNow : false
      this.showSwitchToYearlyBilling = params.showSwitchToYearlyBilling ? params.showSwitchToYearlyBilling : false
      this.showSwitchToMonthlyBilling = params.showSwitchToMonthlyBilling ? params.showSwitchToMonthlyBilling : false
      this.showCancelPlan = params.showCancelPlan ? params.showCancelPlan : false
      this.showRestorePlan = params.showRestorePlan ? params.showRestorePlan : false
    },
    resetInterfaceFlags(params) {
      this.recurringPaymentFailed = params.recurringPaymentFailed ? params.recurringPaymentFailed : false
      this.initialPaymentFailed = params.initialPaymentFailed ? params.initialPaymentFailed : false
    },
    updateDaysLeft(days) {
      if (days) {
        if (days > 1) {
          this.planTitleAddon = this.$tc('daysleft', 2, { count: days })
        } else if (days > 0) {
          this.planTitleAddon = this.$tc('daysleft', 1, { count: days })
        } else {
          this.planTitleAddon = this.$tc('duringthetrial', 0)
        }
      } else {
        this.planTitleAddon = ''
      }
    },
    updateTrialText(days) {
      if (days > 1) {
        this.planTagline = this.$tc('duringthetrial', 2, { count: days, members: this.texts().maxMembersFree })
      } else if (days > 0) {
        this.planTagline = this.$tc('duringthetrial', 1, { count: days, members: this.texts().maxMembersFree })
      } else {
        this.planTagline = this.$tc('daysleft', 0)
      }
    },
    async upgradePlanClicked() {
      EventBus.$emit('show-upgrade-plan', {
        teamId: this.team.teamId,
        plan: this.plan,
        isTrial: this.isTrial,
        cardBrand: this.cardBrand,
        cardLast4: this.cardLast4,
        country: this.country,
        isBillingInfo: this.isBillingInfo,
        businessPurchase: this.vatId == '' ? false : true,
        currency: this.currency,
      })
    },
    async restorePlanClicked() {
      this.loading = true
      let res = await this.restoreSubscription(this.team.teamId)
      await this.updatePaymentDetails()
      this.loading = false
      // console.log('restoreSubscription response', res)
      if (res.status === 'ok') {
        EventBus.$emit('new-info-bar-msg', { msg: this.$t('subscriptionRestoredSuccessfuly') })
      } else {
        this.showError('errorWhileRestoringSubscription')
      }
    },
    async switchToPlanClicked() {
      await this.$router.push({
        path: '/payment',
        name: 'Payment',
        component: Payment,
        query: {
          teamId: this.team.teamId,
          paymentId: this.paymentId,
          plan: this.plan,
          isTrial: this.isTrial,
          cardBrand: this.cardBrand,
          cardLast4: this.cardLast4,
          country: this.country,
          isBillingInfo: this.isBillingInfo,
          businessPurchase: this.vatId == '' ? false : true,
          currency: this.currency,
        },
      })
    },
    async updatePaymentDetails() {
      let navigationlangugage = navigator.language.toUpperCase()
      const language = this.$i18n.locale
      let moment = require('moment-timezone')
      let userTimezone = moment.tz.guess()
      let countryCode = this.$store.state.timezonesGlobal.find((x) => x.value === userTimezone).countryCode
      // console.log('getPaymentDetails arg', this.team.teamId, navigationlangugage, country, language)
      let res = await this.getPaymentDetails(this.team.teamId, navigationlangugage, countryCode, language)

      if (res.status === 'ok') {
        // console.log('getPaymentDetails res', res)

        this.isTrial = res.isTrial

        let paymentMethods = res.paymentMethod
        if (res.billingInformation !== null && paymentMethods && paymentMethods.length !== 0) {
          let card = paymentMethods[0].card
          this.cardLast4 = card.last4
          this.paymentId = paymentMethods[0].id
          this.cardBrand = card.brand.charAt(0).toUpperCase() + card.brand.slice(1)
          this.cardNumber = '**** **** **** ' + this.cardLast4
          this.paymentMethodFetched = true
          this.showUpdateBillingButton = true
        } else {
          this.showUpdateBillingButton = false
        }
        this.subscriptionStatus = res.subscriptionStatus

        this.howMuchSave = res.priceSaveYearlyWithTaxFormatted
        this.activeMembersCount = res.activeMembersCount
        this.pricePerMemberNoTaxFormatted = res.pricePerMemberNoTaxFormatted
        this.plan = res.plan
        this.pendingPlan = res.pendingPlan
        this.currency = res.currency
        this.updateDaysLeft(res.daysLeft)

        if (res.upcomingInvoiceAmountFormatted && res.upcomingInvoiceDateFormatted && res.pricePerMemberWithTaxFormatted) {
          this.upcomingInvoiceAmount = res.upcomingInvoiceAmountFormatted
          this.upcomingInvoiceDateFormatted = res.upcomingInvoiceDateFormatted
          this.pricePerMemberWithTaxFormatted = res.pricePerMemberWithTaxFormatted
        } else {
          this.upcomingInvoiceAmount = ''
          this.upcomingInvoiceDateFormatted = ''
          this.pricePerMemberWithTaxFormatted = ''
        }

        if ((res.plan === 'standardMonthly' || res.plan === 'standardYearly') && res.subscriptionStatus != 'canceled') {
          if (res.isTrial) {
            this.planTitle = this.$t('standardPlanTrial')
            this.updateTrialText(res.daysLeft)
            this.setUpgradePlanButtonsVisibility({ showUpgradeNow: true })
            this.resetInterfaceFlags({})
          } else {
            if (res.subscriptionStatus == 'active') {
              if (res.validUntil && res.validUntil != '') {
                this.planTitle = this.$t('standardPlan')
                this.planTagline = this.$t('thePlanWasCanceled', { planLowercase: 'standard', date: getlFormatDate(res.validUntil, language), maxUsersFree: 5 })
                this.setUpgradePlanButtonsVisibility({ showRestorePlan: true })
              } else {
                this.planTitle = this.$t('standardPlan')
                this.planTagline = this.$tc(res.plan === 'standardMonthly' ? 'xMembersYPerMonth' : 'xMembersYPerYear', this.activeMembersCount > 1 ? 1 : 0, {
                  x: this.activeMembersCount,
                  y: this.pricePerMemberWithTaxFormatted,
                })
                this.planTotal = ' = ' + res.upcomingInvoiceAmountFormatted + ' ' + this.$tc(res.plan === 'standardMonthly' ? 'perMonth' : 'perYear')
                if (res.plan === 'standardMonthly') {
                  this.setUpgradePlanButtonsVisibility({ showSwitchToYearlyBilling: true, showCancelPlan: true })
                } else {
                  this.setUpgradePlanButtonsVisibility({ showSwitchToMonthlyBilling: true, showCancelPlan: true })
                }
              }
              this.resetInterfaceFlags({})
            } else if (res.subscriptionStatus == 'past_due') {
              this.planTitle = this.$t('standardPlan')
              this.planTagline = this.$t('weAreWaitingForThePayment')
              this.setUpgradePlanButtonsVisibility({ showUpgradeNow: true })
              this.resetInterfaceFlags({ recurringPaymentFailed: true })
            } else if (res.subscriptionStatus == 'incomplete') {
              this.planTitle = this.$t('upgradeToStandardPlanIsPending')
              this.planTagline = this.$t('weAreWaitingForThePayment')
              this.setUpgradePlanButtonsVisibility({ showUpgradeNow: true })
              this.resetInterfaceFlags({ initialPaymentFailed: true })
            } else {
              this.planTitle = this.$t('free')
              this.planTagline = this.$t('upTo5Members')
              this.setUpgradePlanButtonsVisibility({ showUpgradeNow: true })
              this.resetInterfaceFlags({})
            }
          }
        } else {
          this.planTitle = this.$t('free')
          this.planTagline = this.$t('upTo5Members')
          this.setUpgradePlanButtonsVisibility({ showUpgradeNow: true })
          this.resetInterfaceFlags({})
        }

        if (res.billingInformation) {
          this.companyName = res.billingInformation.shipping.name
          this.addressLine1 = res.billingInformation.shipping.address.line1
          this.addressLine2 = res.billingInformation.shipping.address.line2
          this.postalCode = res.billingInformation.shipping.address.postal_code
          this.stateProvince = res.billingInformation.shipping.address.state
          this.city = res.billingInformation.shipping.address.city
          this.postalCodeAndCity = res.billingInformation.shipping.address.postal_code + ' ' + res.billingInformation.shipping.address.city
          this.country = res.billingInformation.shipping.address.country

          if (res.billingInformation.taxId !== undefined) {
            this.vatId = res.billingInformation.taxId.value
            this.businessPurchase = true
          } else {
            this.businessPurchase = false
          }
          this.showUpdateBillingButton = true
          this.isBillingInfo = true
        } else {
          this.isBillingInfo = false
        }

        let pendingPlanBoolean = false
        if (this.pendingPlan) {
          pendingPlanBoolean = true
        }
        this.paymentFailedAlert = this.recurringPaymentFailed || this.initialPaymentFailed || pendingPlanBoolean
        this.$store.commit('changePaymentFailedStatus', this.paymentFailedAlert)
        EventBus.$emit('reload-teams')
        this.loading = false
        return true
      } else {
        // res.status !== 'ok'
        this.showServerError(res)
        return false
      }
    },
    async cancel() {
      this.loading = true
      let res = await this.cancelSubscription(this.team.teamId)
      await this.updatePaymentDetails()
      this.loading = false
      // console.log('cancelSubscription res', res)
      if (res.status === 'ok') {
        EventBus.$emit('new-info-bar-msg', { msg: this.$t('subscriptionWillBeCanceledSoon') })
      } else {
        EventBus.$emit('new-info-bar-msg', {
          msg: this.$t('errorWhileCancelingSubscription'),
          error: true,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
section {
  display: flex; // flexbox is sufficient
  flex-direction: column;
  margin: 35px;

  .single-setting {
    margin-bottom: 60px;
    &:last-of-type {
      margin-bottom: 0;
    }
  }
  #subscription-plan-field {
    grid-row: 1;
  }
  #plan-buttons {
    grid-column: 1 / span 3;

    button {
      margin-right: 18px;
    }
    .clavo-link {
      margin-top: 18px;
    }

    span {
      @include font-rule('averta-regular');
      font-size: 14px;
    }
  }
  #next-invoice-details {
    grid-column: 1 / span 3;
    font-size: 14px;
    margin-top: -24px;
    color: $amethyst;
  }
  #plan-buttons-trial {
    grid-row: 2;
    grid-column: 1 / span 3;

    button {
      margin-right: 18px;
    }

    span {
      @include font-rule('averta-regular');
      font-size: 14px;
    }
  }

  #card-details {
    grid-row: 1;
    position: relative;
  }
  .undefined {
    grid-column: span 3;
    font-size: $font-size-paragraph;
  }
  .payment-method-inside {
    grid-column: 1 / span 3;
    @include font-paragraph;

    #subscription-failed {
      color: $red;
    }
    #payment-buttons {
      margin-top: 24px;

      button {
        padding: 0;
      }

      span {
        @include font-rule('averta-regular');
        font-size: 14px;
        margin-left: 18px;
        @include respond-below(xs) {
          margin-left: 0;
          margin-top: 18px;
        }
      }
    }
  }

  #billing-information {
    div {
      @include font-rule('averta-regular');
      font-size: 14px;
      margin-bottom: 10px;
    }

    button {
      margin-top: 14px;
    }
  }

  #invoice-history {
    p {
      grid-column: 1 / span 3;
    }
  }

  @include respond-below(xs) {
    .clavo-link {
      text-align: center;
      //width: 100%;
      display: block;
    }
    .clavo-button {
      width: 100%;
      &.first {
        margin-bottom: 10px;
      }
    }
  }
  @include respond-above(xs) {
    .clavo-button {
      &.first {
        margin-right: 18px;
      }
    }
  }

  .strong-description {
    p {
      color: $black !important;
    }
  }

  .list {
    grid-column: 1 / span 3;
    display: table;
    width: 100%;
    margin-bottom: 20px;

    .headers {
      display: table-header-group;
      background-color: white;
      position: sticky;
      top: 0px;
      @include label;

      .header {
        @include font-rule('averta-semibold');
        padding: 12px 0px 12px 0px;
        display: table-cell;
        border-bottom: 1px solid $soap;
        vertical-align: middle;
      }
    }

    .row {
      display: table-row;
      @include font-paragraph;
      .item {
        padding: 16px 0px 16px 0px;
        display: table-cell;
        vertical-align: middle;
        border-bottom: 1px solid $soap;
        &.date {
          font-family: $bold;
        }

        &.pdf {
          text-align: right;
          font-family: $bold;

          &.inactive {
            cursor: default;
            pointer-events: none;
          }
        }
      }
    }
  }
}

@include respond-below(sm) {
  section {
    margin: 20px;
    //margin-top: 35px;
  }
}
</style>
